import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Save, Eye, RefreshCw, FileText, AlertCircle } from 'lucide-react';
import api from '../../lib/api';

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([
    {
      id: 'booking_confirmation',
      name: 'Booking Confirmation',
      subject: 'Booking Confirmation - {{property_name}} Unit {{unit_number}}',
      description: 'Sent to customer, agent, and admin when a booking is submitted',
      recipients: ['Customer', 'Agent', 'Admin'],
      variables: ['property_name', 'unit_number', 'buyer_name', 'buyer_ic', 'buyer_phone', 'buyer_email', 'agent_name', 'agent_code', 'booking_ref', 'booking_date', 'property_price'],
      html_template: `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #4E50FF, #6366F1); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .info-box { background: white; padding: 20px; border-radius: 8px; margin: 15px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .label { color: #6b7280; font-size: 12px; text-transform: uppercase; margin-bottom: 4px; }
        .value { color: #111827; font-size: 16px; font-weight: 600; }
        .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1 style="margin: 0; font-size: 24px;">Sam Rumah Property</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9;">Booking Confirmation</p>
        </div>
        <div class="content">
            <div class="info-box">
                <div class="label">Booking Reference</div>
                <div class="value">{{booking_ref}}</div>
            </div>
            <div class="info-box">
                <div class="label">Property</div>
                <div class="value">{{property_name}}</div>
            </div>
            <div class="info-box">
                <div class="label">Unit</div>
                <div class="value">{{unit_number}}</div>
            </div>
            <div class="info-box">
                <div class="label">Buyer</div>
                <div class="value">{{buyer_name}}</div>
            </div>
            <div class="info-box">
                <div class="label">Agent</div>
                <div class="value">{{agent_name}} ({{agent_code}})</div>
            </div>
        </div>
        <div class="footer">
            <p>SP SAM Property Sdn Bhd</p>
        </div>
    </div>
</body>
</html>`,
    },
    {
      id: 'spa_signed',
      name: 'SPA Signed Notification',
      subject: 'SPA Signed - {{property_name}} Unit {{unit_number}}',
      description: 'Sent when the Sale & Purchase Agreement is signed',
      recipients: ['Customer', 'Agent'],
      variables: ['property_name', 'unit_number', 'buyer_name', 'spa_date', 'booking_ref'],
      html_template: `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #10b981; color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .success-badge { display: inline-block; padding: 6px 12px; background: #10b981; color: white; border-radius: 20px; font-size: 12px; font-weight: 600; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>SPA Signed Successfully</h1>
        </div>
        <div class="content">
            <p>Dear {{buyer_name}},</p>
            <p>Your Sale & Purchase Agreement for {{property_name}} - {{unit_number}} has been signed on {{spa_date}}.</p>
            <p>Reference: {{booking_ref}}</p>
        </div>
    </div>
</body>
</html>`,
    },
    {
      id: 'commission_approved',
      name: 'Commission Approved',
      subject: 'Commission Approved - {{amount}}',
      description: 'Sent to agent when their commission claim is approved',
      recipients: ['Agent'],
      variables: ['agent_name', 'amount', 'booking_ref', 'property_name', 'approval_date'],
      html_template: `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .amount { font-size: 32px; font-weight: bold; color: #10b981; text-align: center; margin: 20px 0; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Commission Approved!</h1>
        </div>
        <div class="content">
            <p>Dear {{agent_name}},</p>
            <div class="amount">{{amount}}</div>
            <p>Your commission claim for {{property_name}} (Ref: {{booking_ref}}) has been approved and credited to your wallet.</p>
            <p>Approval Date: {{approval_date}}</p>
        </div>
    </div>
</body>
</html>`,
    },
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [editingSubject, setEditingSubject] = useState(templates[0].subject);
  const [editingHtml, setEditingHtml] = useState(templates[0].html_template);
  const [previewMode, setPreviewMode] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    resend_api_key: '',
    sender_email: 'onboarding@resend.dev',
    sender_name: 'Sam Rumah Property',
  });
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const res = await api.get('/api/config');
      if (res.data?.email) {
        setEmailConfig(prev => ({ ...prev, ...res.data.email }));
      }
    } catch (error) {
      console.log('Email config not found, using defaults');
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditingSubject(template.subject);
    setEditingHtml(template.html_template);
    setPreviewMode(false);
  };

  const handleSaveTemplate = () => {
    setTemplates(prev => prev.map(t => 
      t.id === selectedTemplate.id 
        ? { ...t, subject: editingSubject, html_template: editingHtml }
        : t
    ));
    toast.success('Template saved successfully');
  };

  const handleSaveConfig = async () => {
    setConfigLoading(true);
    try {
      await api.put('/api/config', { email: emailConfig });
      toast.success('Email configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const getPreviewHtml = () => {
    let html = editingHtml;
    const sampleData = {
      property_name: 'Skyline Residence',
      unit_number: 'A-15-08',
      buyer_name: 'John Doe',
      buyer_ic: '901234-56-7890',
      buyer_phone: '+60 12-345 6789',
      buyer_email: 'john@example.com',
      agent_name: 'Sarah Agent',
      agent_code: 'AG-1234',
      booking_ref: 'BK-ABC123',
      booking_date: '06 Feb 2026',
      property_price: 'RM 850,000',
      spa_date: '06 Feb 2026',
      amount: 'RM 8,500',
      approval_date: '06 Feb 2026',
    };
    
    Object.entries(sampleData).forEach(([key, value]) => {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    
    return html;
  };

  return (
    <div className="space-y-6" data-testid="admin-email-templates">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Mail className="h-6 w-6 text-[#4E50FF]" />
          Email Templates
        </h1>
        <p className="text-slate-500 mt-1">Customize email notifications sent to users</p>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Template List */}
            <Card className="border shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-700">Available Templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedTemplate.id === template.id
                        ? 'border-[#4E50FF] bg-[#4E50FF]/5'
                        : 'border-transparent hover:bg-slate-50'
                    }`}
                    data-testid={`template-${template.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className={`h-4 w-4 ${selectedTemplate.id === template.id ? 'text-[#4E50FF]' : 'text-slate-400'}`} />
                      <span className="font-medium text-slate-900 text-sm">{template.name}</span>
                    </div>
                    <p className="text-xs text-slate-500 ml-6">{template.description}</p>
                    <div className="flex gap-1 mt-2 ml-6">
                      {template.recipients.map((r) => (
                        <Badge key={r} variant="secondary" className="text-xs px-2 py-0">
                          {r}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Template Editor */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="border shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium">{selectedTemplate.name}</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={previewMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewMode(!previewMode)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {previewMode ? 'Edit' : 'Preview'}
                    </Button>
                    <Button size="sm" onClick={handleSaveTemplate} className="bg-[#4E50FF] hover:bg-[#4E50FF]/90">
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!previewMode ? (
                    <>
                      <div>
                        <Label className="text-slate-600">Subject Line</Label>
                        <Input
                          value={editingSubject}
                          onChange={(e) => setEditingSubject(e.target.value)}
                          className="mt-1"
                          placeholder="Email subject..."
                        />
                        <p className="text-xs text-slate-400 mt-1">Use {'{{variable}}'} for dynamic content</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">HTML Template</Label>
                        <Textarea
                          value={editingHtml}
                          onChange={(e) => setEditingHtml(e.target.value)}
                          className="mt-1 font-mono text-sm h-96"
                          placeholder="HTML template..."
                        />
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-sm text-slate-600 font-medium mb-2">Available Variables:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.variables.map((v) => (
                            <code key={v} className="bg-slate-200 px-2 py-1 rounded text-xs text-slate-700">
                              {'{{'}{v}{'}}'}
                            </code>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-3 py-2 border-b text-xs text-slate-500">
                        Subject: {editingSubject.replace(/{{(\w+)}}/g, (_, key) => {
                          const samples = { property_name: 'Skyline Residence', unit_number: 'A-15-08', amount: 'RM 8,500' };
                          return samples[key] || key;
                        })}
                      </div>
                      <iframe
                        srcDoc={getPreviewHtml()}
                        className="w-full h-96 bg-white"
                        title="Email Preview"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <Card className="border shadow-sm max-w-xl">
            <CardHeader>
              <CardTitle className="text-base font-medium">Email Service Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Resend API Key Required</p>
                  <p className="text-amber-700 mt-1">
                    To enable email notifications, add your Resend API key. Get one free at{' '}
                    <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">
                      resend.com
                    </a>
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-slate-600">Resend API Key</Label>
                <Input
                  type="password"
                  value={emailConfig.resend_api_key}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, resend_api_key: e.target.value }))}
                  className="mt-1"
                  placeholder="re_xxxx..."
                />
              </div>

              <div>
                <Label className="text-slate-600">Sender Email</Label>
                <Input
                  value={emailConfig.sender_email}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_email: e.target.value }))}
                  className="mt-1"
                  placeholder="noreply@yourdomain.com"
                />
                <p className="text-xs text-slate-400 mt-1">Must be a verified domain in Resend</p>
              </div>

              <div>
                <Label className="text-slate-600">Sender Name</Label>
                <Input
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                  className="mt-1"
                  placeholder="Sam Rumah Property"
                />
              </div>

              <Button 
                onClick={handleSaveConfig} 
                disabled={configLoading}
                className="bg-[#4E50FF] hover:bg-[#4E50FF]/90"
              >
                {configLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Configuration
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminEmailTemplates;
